/**
 * useMFA Hook
 *
 * React hook for Multi-Factor Authentication (TOTP/2FA).
 * Compatible with Google Authenticator, Authy, 1Password, Microsoft Authenticator.
 *
 * @example
 * const { beginEnableMFA, completeEnableMFA, verifyMFA } = useMFA();
 */

import { useState } from 'react';
import { BackendAuth } from '@/lib/auth-backend';

export function useMFA() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Begin MFA setup - generate TOTP secret and QR code
   *
   * @returns {Promise<object>} { secret, qr_code, manual_entry }
   */
  const beginEnableMFA = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await BackendAuth.apiCallJson('/api/auth/mfa/enable/begin', {
        method: 'POST'
      });

      // console.log('[MFA] Setup initiated:', response.issuer);

      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('[MFA] Setup failed:', err);
      const errorMessage = err.message || 'Failed to initiate MFA setup';
      setError(errorMessage);
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  /**
   * Complete MFA setup - verify TOTP code and enable MFA
   *
   * @param {string} secret - TOTP secret from beginEnableMFA
   * @param {string} code - 6-digit code from authenticator app
   * @returns {Promise<object>} { backup_codes }
   */
  const completeEnableMFA = async (secret, code) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await BackendAuth.apiCallJson('/api/auth/mfa/enable/complete', {
        method: 'POST',
        body: JSON.stringify({ secret, code }),
      });

      // console.log('[MFA] Enabled successfully - backup codes generated');

      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('[MFA] Enable failed:', err);
      const errorMessage = err.message || 'Invalid verification code';
      setError(errorMessage);
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  /**
   * Verify MFA code during login
   *
   * @param {string} tempToken - Temporary token from login
   * @param {string} code - 6-digit TOTP code or 8-character backup code
   * @returns {Promise<object>} { access_token, refresh_token }
   */
  const verifyMFA = async (tempToken, code) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await BackendAuth.apiCallJson('/api/auth/mfa/verify', {
        method: 'POST',
        body: JSON.stringify({ temp_token: tempToken, code }),
      });

      // console.log('[MFA] Verification successful');

      // Store tokens in localStorage
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);

      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('[MFA] Verification failed:', err);
      const errorMessage = err.message || 'Invalid MFA code';
      setError(errorMessage);
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  /**
   * Disable MFA
   *
   * @param {string} password - Current password
   * @param {string} code - Current MFA code
   * @returns {Promise<object>} Success confirmation
   */
  const disableMFA = async (password, code) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await BackendAuth.apiCallJson('/api/auth/mfa/disable', {
        method: 'POST',
        body: JSON.stringify({ password, code }),
      });

      // console.log('[MFA] Disabled successfully');

      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('[MFA] Disable failed:', err);
      const errorMessage = err.message || 'Failed to disable MFA';
      setError(errorMessage);
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  /**
   * Get MFA status for current user
   *
   * @returns {Promise<object>} MFA status
   */
  const getMFAStatus = async () => {
    try {
      const response = await BackendAuth.apiCallJson('/api/auth/mfa/status');
      return response;
    } catch (err) {
      console.error('[MFA] Failed to get status:', err);
      throw new Error('Failed to get MFA status');
    }
  };

  /**
   * Regenerate backup codes
   *
   * @returns {Promise<object>} { backup_codes }
   */
  const regenerateBackupCodes = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await BackendAuth.apiCallJson('/api/auth/mfa/regenerate-backup-codes', {
        method: 'POST'
      });

      // console.log('[MFA] Backup codes regenerated');

      setIsLoading(false);
      return response;
    } catch (err) {
      console.error('[MFA] Failed to regenerate backup codes:', err);
      const errorMessage = err.message || 'Failed to regenerate backup codes';
      setError(errorMessage);
      setIsLoading(false);
      throw new Error(errorMessage);
    }
  };

  return {
    beginEnableMFA,
    completeEnableMFA,
    verifyMFA,
    disableMFA,
    getMFAStatus,
    regenerateBackupCodes,
    isLoading,
    error,
  };
}
